/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// USE AVAILABLE ESTIMATES - Get approved estimates without invoices
// ============================================================================

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient } from "@/lib/api/api-client";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import { estimateAvailabilityQueryKeys } from "@/lib/query/query-keys";
import type { Database } from "@/lib/supabase/types";
import { fetchAllPagesCustom } from "@/lib/utils/fetch-all-pages";
import { logger } from "@/lib/utils/logger";
import { useQuery } from "@tanstack/react-query";
import { filterEstimatesWithoutInvoices } from "@/lib/business-logic/invoices/availability";
import { useMemo } from "react";

type EstimateRow = Database["public"]["Tables"]["estimates"]["Row"];

export interface AvailableEstimate extends EstimateRow {
  clients?: {
    client_id: string;
    name: string | null;
    company_name: string | null;
  } | null;
  projects?: {
    project_id: string;
    name: string | null;
  } | null;
}

interface UseAvailableEstimatesParams {
  clientId?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch approved estimates that don't have invoices yet
 */
export function useAvailableEstimates(
  params: UseAvailableEstimatesParams = {},
) {
  const { getToken } = useAuthSafe();
  const { clientId, enabled = true } = params;
  const { organizationId, queryEnabled } = useOrganizationQueryGuard({
    enabled,
    debugKey: "useAvailableEstimates",
  });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const queryKey = useMemo(
    () =>
      estimateAvailabilityQueryKeys.list(organizationId ?? undefined, clientId),
    [organizationId, clientId],
  );

  const {
    data: estimates,
    isLoading,
    error,
  } = useQuery<AvailableEstimate[]>({
    queryKey,
    enabled: queryEnabled,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      // Fetch all approved estimates using pagination
      const estimatesBaseParams = new URLSearchParams({
        status: "APPROVED",
        fields: "standard",
        ...(clientId && { clientId }),
      });

      const allApprovedEstimates = await fetchAllPagesCustom<EstimateRow>(
        api,
        "/api/estimates",
        estimatesBaseParams,
        (data) => {
          // API returns { success: true, data: { estimates: [...], metrics: {...} }, pagination: {...} }
          const response = data as
            | {
                data?: { estimates?: EstimateRow[] };
                pagination?: { total: number; page: number; limit: number };
              }
            | undefined;
          return response?.data?.estimates ?? [];
        },
        (data) => {
          const response = data as
            | {
                pagination?: { total: number; page: number; limit: number };
              }
            | undefined;
          return response?.pagination ?? null;
        },
      );

      if (allApprovedEstimates.length === 0) {
        return [];
      }

      // Fetch only invoices linked to the approved estimates we found.
      // Scoped via estimate_ids filter to avoid fetching the entire invoice history.
      const estimateIds = allApprovedEstimates
        .map((e) => (e as Record<string, unknown>).estimate_id as string)
        .filter(Boolean);

      let allInvoices: Array<{ estimate_id: string | null }>;
      try {
        const invoicesBaseParams = new URLSearchParams();
        if (estimateIds.length > 0) {
          invoicesBaseParams.set("estimate_ids", estimateIds.join(","));
        }
        allInvoices = await fetchAllPagesCustom<{ estimate_id: string | null }>(
          api,
          "/api/invoices",
          invoicesBaseParams,
          (data) => {
            const response = data as
              | {
                  data?: Array<{ estimate_id: string | null }>;
                  pagination?: { total: number; page: number; limit: number };
                }
              | undefined;
            return response?.data ?? [];
          },
          (data) => {
            const response = data as
              | {
                  pagination?: { total: number; page: number; limit: number };
                }
              | undefined;
            return response?.pagination ?? null;
          },
        );
      } catch (error) {
        logger.error("Failed to fetch invoices for available estimates", {
          error,
        });
        // If we can't fetch invoices, return all approved estimates
        // (better to show more than less — user sees a warning at worst)
        return allApprovedEstimates as AvailableEstimate[];
      }

      return filterEstimatesWithoutInvoices(
        allApprovedEstimates as AvailableEstimate[],
        allInvoices,
      ) as AvailableEstimate[];
    },
  });

  return {
    estimates: estimates ?? [],
    isLoading,
    error: error ? String(error) : null,
  };
}
